import styled from "styled-components";
import { COLORS, LAYER_COLORS, TEXT_COLORS, BORDER_COLORS } from "./tokens";

export const DashboardSummary = styled.section`
  padding: 24px 0;
  border-bottom: 1px solid #15151b;
  @media (max-width: 760px) {
    padding: 20px 0 0;
    border-bottom: 0;
  }
`;

export const DashboardHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 16px;
  row-gap: 8px;
  margin: 0;
  && h2 {
    font-size: 22px;
    font-weight: 300;
    margin: 0;
  }
  .identity {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .tier {
    border-radius: 4px;
    background: rgba(136, 86, 255, 0.12);
    color: #b9a8ff;
    padding: 4px 7px;
    font-size: 11px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .personal-fees {
    border: 0;
    background: transparent;
    color: ${COLORS.highlight.one};
    padding: 0;
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;
  }
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
    .personal-fees {
      min-height: 44px;
    }
    && h2 {
      font-size: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

export const DashboardKpis = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 24px;
  > div {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    padding: 16px;
    border: 1px solid ${BORDER_COLORS.two};
    border-radius: 8px;
    background: ${LAYER_COLORS.one};
    min-height: 94px;
  }
  small {
    color: ${TEXT_COLORS.three};
    font-size: 11px;
    line-height: 12px;
  }
  .value-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
  }
  strong {
    margin: 0;
    font-size: 28px;
    line-height: 32px;
    font-weight: 300;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
  button {
    min-height: 32px !important;
    height: 32px;
    padding: 0 16px;
    white-space: nowrap;
    border: 0;
    border-radius: 8px;
    background: ${COLORS.highlight.one};
    color: #000;
    font-size: 13px;
    font-weight: 500;
  }
  button:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }
  .holdback-detail {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 4px;
  }
  .holdback-detail small {
    white-space: nowrap;
  }
  .holdback {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    border-radius: 6px;
    background: ${LAYER_COLORS.two};
    padding: 4px 8px;
    color: ${TEXT_COLORS.one};
    font-size: 12px;
    white-space: nowrap;
  }
  .holdback::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${COLORS.highlight.one};
  }
  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 16px;
    > div {
      border-radius: 12px;
      min-height: 0;
    }
    > div:first-child {
      grid-column: 1 / -1;
      gap: 12px;
    }
    > div:first-child .value-row,
    > div:not(:first-child) .value-row {
      align-items: stretch;
      flex-direction: column;
    }
    > div:first-child strong {
      font-size: 32px;
      line-height: 36px;
    }
    > div:first-child button {
      width: 100%;
      min-height: 44px !important;
      height: 44px;
    }
    > div:not(:first-child) strong {
      font-size: 20px;
      line-height: 24px;
    }
    > div:not(:first-child) button {
      min-height: 44px !important;
      height: 44px;
      padding: 0 12px;
    }
    .holdback-detail {
      align-items: flex-start;
    }
    .holdback-detail small {
      display: none;
    }
  }
`;

export const DashboardTabs = styled.div`
  display: flex;
  gap: 8px;
  margin: 32px 0 0;
  flex-wrap: wrap;
  button {
    min-height: 32px;
    padding: 0 16px;
    border: 0;
    border-radius: 20px;
    background: ${LAYER_COLORS.two};
    color: ${TEXT_COLORS.two};
    font-size: 12px;
    font-weight: 500;
  }
  button[aria-selected="true"] {
    color: ${COLORS.highlight.one};
    box-shadow: 0 0 0 1px ${COLORS.highlight.one} inset;
  }
  button:focus-visible {
    outline: 2px solid ${COLORS.highlight.one};
    outline-offset: 2px;
  }
  @media (max-width: 760px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 24px;
    button {
      min-height: 44px;
      font-size: 13px;
    }
  }
`;

export const CountTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  background: rgba(76, 215, 244, 0.08);
  color: ${COLORS.highlight.one};
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${COLORS.highlight.one};
  }
`;

export const ReferralHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  .title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  && h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 300;
  }
  @media (max-width: 760px) {
    flex-direction: column;
    align-items: stretch;
    .title {
      justify-content: space-between;
      gap: 8px;
    }
    && h2 {
      font-size: 20px;
    }
    button {
      width: 100%;
      min-height: 44px !important;
    }
  }
`;

export const LinkCards = styled.div`
  display: none;
  @media (max-width: 760px) {
    display: grid;
    gap: 12px;
    > article {
      padding: 16px;
      border: 1px solid ${BORDER_COLORS.two};
      border-radius: 8px;
      background: ${LAYER_COLORS.one};
      min-width: 0;
    }
    .top,
    .actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
    .top strong {
      font-size: 16px;
      overflow-wrap: anywhere;
    }
    .stats {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
      margin: 16px 0;
    }
    .stats small {
      display: block;
      color: ${TEXT_COLORS.three};
      font-size: 11px;
    }
    .stats span {
      display: block;
      font-size: 12px;
      overflow-wrap: anywhere;
    }
    .actions button {
      min-height: 44px !important;
      flex: 1;
    }
    .actions .copy-button {
      flex: 0 0 44px;
      width: 44px;
      padding: 0;
      justify-content: center;
    }
  }
`;

export const DesktopOnly = styled.div`
  && table th,
  && table td {
    height: 40px;
    padding: 0 8px;
    width: 1%;
    white-space: nowrap;
  }
  && table .spacer {
    width: auto;
    min-width: 16px;
    padding: 0;
  }
  && table th {
    height: 32px;
    padding: 0 8px;
    color: ${TEXT_COLORS.three};
  }
  && table th:last-child,
  && table td:last-child {
    width: 1%;
    text-align: right;
    white-space: nowrap;
  }
  && table td:last-child > div {
    justify-content: flex-end;
    flex-wrap: nowrap;
  }
  && table td:last-child button {
    min-height: 32px;
    height: 32px;
    padding: 0 12px;
  }
  && table td:last-child .copy-button {
    width: 32px;
    min-width: 32px;
    padding: 0;
    justify-content: center;
  }
  @media (max-width: 760px) {
    display: none;
  }
`;

export const InlineStatus = styled.small`
  display: block;
  margin-top: 4px;
  color: ${COLORS.highlight.one};
  font-size: 11px;
`;

export const AnnouncedStatus = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const FeeRuleNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 24px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 122, 0, 0.24);
  border-radius: 8px;
  background: rgba(255, 122, 0, 0.08);
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  &::before {
    content: "";
    width: 6px;
    height: 6px;
    flex: 0 0 6px;
    margin-top: 7px;
    border-radius: 50%;
    background: #ff7a00;
  }
`;

export const WalletIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 12px 16px;
  border-radius: 12px;
  background: #1e1e24;
  color: #fff;
  font-size: 13px;
  line-height: 20px;
  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  button {
    flex-shrink: 0;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    margin-left: auto;
  }
`;
